import { LDateTime } from '@app/components/date/LDateTime';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import { webApi } from '@mi-front/api-clients';
import { formatDuration } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { EventTypeChip } from '../shared/EventTypeChip';
import { useViewModel } from '../store/useViewModel';
import { IssuesTableRow } from './issues-table-row';
import { useIssuesTableRows } from './useIssuesTableRows';
import { OpenLoggerRowAction } from './OpenLoggerRowAction';

const messages = defineMessages({
  colStation: {
    id: 'logger.reports.streamQuality.availability.table.col.station',
    defaultMessage: 'Emisora',
  },
  colIssueType: {
    id: 'logger.reports.streamQuality.availability.table.col.issueType',
    defaultMessage: 'Tipo',
  },
  colStart: {
    id: 'logger.reports.streamQuality.availability.table.col.start',
    defaultMessage: 'Inicio',
  },
  colDuration: {
    id: 'logger.reports.streamQuality.availability.table.col.duration',
    defaultMessage: 'Duración',
  },
  colEnd: {
    id: 'logger.reports.streamQuality.availability.table.col.end',
    defaultMessage: 'Fin',
  },
  colActions: {
    id: 'common.text.actions',
    defaultMessage: 'Acciones',
  },
});

function getRowDurationMs(row: IssuesTableRow): number {
  const { payload } = row;
  if (payload.type === 'issue') {
    return Math.max(0, Math.round(payload.issue.durationSeconds * 1000));
  }
  if (payload.type === 'gap' && row.gapDurationMs != null) {
    return Math.max(0, row.gapDurationMs);
  }
  return Math.max(0, payload.end.getTime() - payload.start.getTime());
}

export type IssuesTableProps = {
  report: webApi.StreamQualityReportResponseDto;
};

/**
 * Paginated table of stream gaps and audio quality issues for the report window.
 * Rows are filtered by active station status chips and overlap with the shared visible range.
 */
export function IssuesTable({ report }: IssuesTableProps) {
  const { $t } = useIntl();
  const finder = useStationsFinder();
  const { filteredStationIds: stationIds, visibleRange } = useViewModel(report);
  const rows = useIssuesTableRows(report, stationIds, visibleRange);

  const columns = useMemo(
    () => [
      {
        id: 'station',
        header: $t(messages.colStation),
        enableSorting: false,
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => {
          const sid = row.original.stationId;
          const station = finder(sid) ?? { id: sid, name: sid, slug: sid };
          return <StationLabel station={station} />;
        },
        size: 220,
      },
      {
        id: 'issueType',
        header: $t(messages.colIssueType),
        enableSorting: false,
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => (
          <EventTypeChip payload={row.original.payload} withBottomMargin={false} />
        ),
        size: 180,
      },
      {
        id: 'start',
        accessorFn: (row: IssuesTableRow) => row.payload.start.getTime(),
        header: $t(messages.colStart),
        enableSorting: true,
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => {
          const sid = row.original.stationId;
          const stationTz = finder(sid)?.timezone;
          return (
            <Typography variant="body2" component="span">
              <LDateTime value={row.original.payload.start.toISOString()} tz={stationTz} showTz />
            </Typography>
          );
        },
        size: 200,
      },
      {
        id: 'end',
        accessorFn: (row: IssuesTableRow) => row.payload.end.getTime(),
        header: $t(messages.colEnd),
        enableSorting: true,
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => {
          const sid = row.original.stationId;
          const stationTz = finder(sid)?.timezone;
          return (
            <Typography variant="body2" component="span">
              <LDateTime value={row.original.payload.end.toISOString()} tz={stationTz} showTz />
            </Typography>
          );
        },
        size: 200,
      },
      {
        id: 'duration',
        accessorFn: (row: IssuesTableRow) => getRowDurationMs(row),
        /** Force numeric order; `auto` can pick alphanumeric and sort by rendered-like strings. */
        sortingFn: 'basic',
        header: $t(messages.colDuration),
        enableSorting: true,
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => (
          <Typography variant="body2" component="span">
            {formatDuration(getRowDurationMs(row.original))}
          </Typography>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: $t(messages.colActions),
        enableSorting: false,
        accessorFn: () => '',
        Cell: ({ row }: { row: { original: IssuesTableRow } }) => {
          const sid = row.original.stationId;
          const station = finder(sid) ?? { id: sid, name: sid, slug: sid };
          return <OpenLoggerRowAction station={station} at={row.original.payload.start} />;
        },
        size: 72,
      },
    ],
    [$t, finder],
  );

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <MrtTable<IssuesTableRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        enableSorting
        memoizeColumns={false}
        initialState={{
          pagination: { pageSize: 20, pageIndex: 0 },
          /** Tighter row height and cell padding (MRT “small table” feel). */
          density: 'compact',
        }}
      />
    </Box>
  );
}
